/**
 * NGA Emission Factors Calculator
 * 
 * UNIT CONVENTION:
 * - All factor_values are in kg CO2-e per input unit
 * - Input units vary by category (kWh, L, GJ, kg, t, pax-km, nights)
 * - Output is ALWAYS in kg CO2-e
 * - Dashboard displays tCO2-e = kg CO2-e ÷ 1000
 * 
 * Source: National Greenhouse Accounts Factors (DCCEEW)
 * Factors are applied according to the applicable financial year
 */

// Australian States/Territories
export const AUSTRALIAN_STATES = ['NSW', 'VIC', 'QLD', 'WA', 'SA', 'TAS', 'ACT', 'NT'];

export const STATE_NAMES = {
  'NSW': 'New South Wales',
  'VIC': 'Victoria',
  'QLD': 'Queensland',
  'WA': 'Western Australia',
  'SA': 'South Australia',
  'TAS': 'Tasmania',
  'ACT': 'Australian Capital Territory',
  'NT': 'Northern Territory'
};

// WA and NT have multiple grids - these are the sub-regions
export const GRID_REGIONS = {
  'WA': {
    'WA_SWIS': 'South West Interconnected System (Perth, SW WA)',
    'WA_NWIS': 'North West Interconnected System (Pilbara)'
  },
  'NT': {
    'NT_DKIS': 'Darwin Katherine Interconnected System',
    'NT_AIUS': 'Alice Springs / Tennant Creek (isolated)'
  }
};

// ============ SCOPE 2 - Electricity Grid Factors by Financial Year ============
// Each year's factors come from that year's NGA publication
// Format: factor_year -> state -> factor (kg CO2-e per kWh)

const ELECTRICITY_FACTORS_BY_YEAR = {
  // FY 2025-26 (NGA Factors 2025)
  2026: {
    'QLD': 0.67, 'NSW': 0.64, 'ACT': 0.64, 'VIC': 0.78, 'SA': 0.22, 'TAS': 0.20,
    'WA': 0.50, 'WA_SWIS': 0.50, 'WA_NWIS': 0.56,
    'NT': 0.56, 'NT_DKIS': 0.56, 'NT_AIUS': 0.56
  },
  // FY 2024-25 (NGA Factors 2024)
  2025: {
    'QLD': 0.71, 'NSW': 0.66, 'ACT': 0.66, 'VIC': 0.77, 'SA': 0.23, 'TAS': 0.15,
    'WA': 0.51, 'WA_SWIS': 0.51, 'WA_NWIS': 0.61,
    'NT': 0.56, 'NT_DKIS': 0.56, 'NT_AIUS': 0.56
  },
  // FY 2023-24 (NGA Factors 2023)
  2024: {
    'QLD': 0.73, 'NSW': 0.68, 'ACT': 0.68, 'VIC': 0.79, 'SA': 0.25, 'TAS': 0.12,
    'WA': 0.53, 'WA_SWIS': 0.53, 'WA_NWIS': 0.61,
    'NT': 0.54, 'NT_DKIS': 0.54, 'NT_AIUS': 0.54
  },
  // FY 2022-23 (NGA Factors 2022)
  2023: {
    'QLD': 0.73, 'NSW': 0.73, 'ACT': 0.73, 'VIC': 0.85, 'SA': 0.25, 'TAS': 0.17,
    'WA': 0.51, 'WA_SWIS': 0.51, 'WA_NWIS': 0.61,
    'NT': 0.54, 'NT_DKIS': 0.54, 'NT_AIUS': 0.54
  },
  // FY 2021-22 (NGA Factors 2021)
  2022: {
    'QLD': 0.73, 'NSW': 0.79, 'ACT': 0.79, 'VIC': 0.96, 'SA': 0.30, 'TAS': 0.17,
    'WA': 0.56, 'WA_SWIS': 0.56, 'WA_NWIS': 0.61,
    'NT': 0.57, 'NT_DKIS': 0.57, 'NT_AIUS': 0.57
  }
};

// Fuel factors by year (relatively stable, but tracked for accuracy)
const FUEL_FACTORS_BY_YEAR = {
  2026: { 'petrol': 2.31, 'diesel': 2.71, 'lpg': 1.57, 'e10': 2.12, 'blended': 2.50 },
  2025: { 'petrol': 2.31, 'diesel': 2.71, 'lpg': 1.57, 'e10': 2.12, 'blended': 2.50 },
  2024: { 'petrol': 2.31, 'diesel': 2.68, 'lpg': 1.55, 'e10': 2.12, 'blended': 2.50 },
  2023: { 'petrol': 2.31, 'diesel': 2.68, 'lpg': 1.55, 'e10': 2.12, 'blended': 2.50 },
  2022: { 'petrol': 2.31, 'diesel': 2.68, 'lpg': 1.55, 'e10': 2.12, 'blended': 2.50 }
};

// Natural gas factors by year (kg CO2-e per GJ)
const NATURAL_GAS_FACTORS_BY_YEAR = {
  2026: 51.53, 2025: 51.53, 2024: 51.53, 2023: 51.53, 2022: 51.53
};

// Get the latest available year (fallback for future years)
const getLatestFactorYear = () => Math.max(...Object.keys(ELECTRICITY_FACTORS_BY_YEAR).map(Number));

// Get the electricity factor for a specific state and financial year
function getElectricityFactor(state, financialYear) {
  const stateUpper = state?.toUpperCase();
  const fy = financialYear || getLatestFactorYear();

  // Find the closest available year (don't go beyond latest available)
  const availableYears = Object.keys(ELECTRICITY_FACTORS_BY_YEAR).map(Number).sort((a, b) => b - a);
  let targetYear = fy;

  if (fy > getLatestFactorYear()) {
    targetYear = getLatestFactorYear();
  } else if (fy < Math.min(...availableYears)) {
    targetYear = Math.min(...availableYears);
  }

  const yearFactors = ELECTRICITY_FACTORS_BY_YEAR[targetYear] || ELECTRICITY_FACTORS_BY_YEAR[getLatestFactorYear()];

  return {
    factor: yearFactors[stateUpper] || yearFactors[stateUpper?.split('_')[0]] || 0.67,
    ngaYear: targetYear,
    ngaSource: `NGA Factors ${targetYear - 1}`
  };
}

// Get fuel factor for a specific fuel type and financial year
function getFuelFactor(fuelType, financialYear) {
  const fy = financialYear || getLatestFactorYear();
  const availableYears = Object.keys(FUEL_FACTORS_BY_YEAR).map(Number).sort((a, b) => b - a);
  let targetYear = fy;

  if (fy > getLatestFactorYear()) {
    targetYear = getLatestFactorYear();
  } else if (fy < Math.min(...availableYears)) {
    targetYear = Math.min(...availableYears);
  }

  const yearFactors = FUEL_FACTORS_BY_YEAR[targetYear] || FUEL_FACTORS_BY_YEAR[getLatestFactorYear()];

  return {
    factor: yearFactors[fuelType] || yearFactors['blended'],
    ngaYear: targetYear,
    ngaSource: `NGA Factors ${targetYear - 1}`
  };
}

// Get natural gas factor for a financial year
function getNaturalGasFactor(financialYear) {
  const fy = financialYear || getLatestFactorYear();
  const availableYears = Object.keys(NATURAL_GAS_FACTORS_BY_YEAR).map(Number).sort((a, b) => b - a);
  let targetYear = fy;

  if (fy > getLatestFactorYear()) {
    targetYear = getLatestFactorYear();
  } else if (fy < Math.min(...availableYears)) {
    targetYear = Math.min(...availableYears);
  }

  return {
    factor: NATURAL_GAS_FACTORS_BY_YEAR[targetYear] || NATURAL_GAS_FACTORS_BY_YEAR[getLatestFactorYear()],
    ngaYear: targetYear,
    ngaSource: `NGA Factors ${targetYear - 1}`
  };
}

// Legacy export for backwards compatibility (uses latest year)
export const STATE_ELECTRICITY_FACTORS = ELECTRICITY_FACTORS_BY_YEAR[getLatestFactorYear()];
const DEFAULT_ELECTRICITY_FACTOR = 0.67; // Australian average

// GreenPower - zero emissions (certified renewable)
const GREENPOWER_FACTOR = 0.0;

// ============ SCOPE 1 - Transport Fuels (kg CO2-e per litre) ============
// NGA Table 9 - includes direct combustion + upstream for full lifecycle
const FUEL_FACTORS = {
  'petrol': 2.31,     // Petrol/gasoline
  'diesel': 2.71,     // Diesel
  'lpg': 1.57,        // LPG (autogas)
  'e10': 2.12,        // E10 (10% ethanol blend)
  'blended': 2.50     // Generic fuel average
};

// ============ SCOPE 1 - Stationary Energy ============
// Natural Gas: kg CO2-e per GJ
const NATURAL_GAS_FACTOR = 51.53;
// Stationary LPG: kg CO2-e per litre
const STATIONARY_LPG_FACTOR = 1.57;
// Stationary Diesel (generators): kg CO2-e per litre
const STATIONARY_DIESEL_FACTOR = 2.71;

// ============ SCOPE 1 - Refrigerants (kg CO2-e per kg leaked) ============
// GWP values from NGA Table 11 (AR5 100-year values)
const REFRIGERANT_FACTORS = {
  'r134a': 1300,    // HFC-134a - car AC, small fridges
  'r410a': 1924,    // R410A - split system AC
  'r32': 677,       // HFC-32 - newer low-GWP AC
  'r404a': 3943,    // R404A - commercial refrigeration (high GWP)
  'r22': 1760       // HCFC-22 - legacy systems (phased out)
};

// ============ SCOPE 3 - Waste (kg CO2-e per tonne) ============
// NGA Tables 15-16
const WASTE_FACTORS = {
  'landfill': 1600,     // Municipal solid waste to landfill
  'commercial': 1300,   // Commercial & industrial waste
  'food': 2100,         // Food waste (high methane)
  'paper': 3300,        // Paper/cardboard
  'garden': 1600,       // Garden/green waste
  'recycling': 210,     // Recycled materials (processing only)
  'compost': 100,       // Composted organics
  'generic': 1600       // Default to municipal
};

// ============ SCOPE 3 - Business Travel (from DEFRA UK) ============
// Flights: kg CO2-e per passenger-km
const FLIGHT_FACTORS = {
  'short': 0.255,     // <3 hours domestic
  'medium': 0.156,    // 3-6 hours
  'long': 0.147       // >6 hours international
};

// Hotel: kg CO2-e per room-night
const HOTEL_FACTOR = 31.6;

// ============ SCOPE 3 - Water (kg CO2-e per kL) ============
const WATER_FACTOR = 0.71;

// ============ SCOPE CLASSIFICATION ============
// Platform supports Scope 1 and Scope 2 only
export const SCOPE_MAPPING = {
  // Scope 1 - Direct emissions
  'fuel': 'Scope 1',
  'fuel_petrol': 'Scope 1',
  'fuel_diesel': 'Scope 1',
  'fuel_lpg': 'Scope 1',
  'fuel_e10': 'Scope 1',
  'natural_gas': 'Scope 1',
  'stationary_lpg': 'Scope 1',
  'stationary_diesel': 'Scope 1',
  'refrigerant_r134a': 'Scope 1',
  'refrigerant_r410a': 'Scope 1',
  'refrigerant_r32': 'Scope 1',
  'refrigerant_r404a': 'Scope 1',
  'refrigerant_r22': 'Scope 1',

  // Scope 2 - Purchased electricity
  'electricity': 'Scope 2',
  'electricity_greenpower': 'Scope 2',
};

// ============ CATEGORY DISPLAY INFO ============
// Scope 1 and Scope 2 categories only
export const CATEGORY_INFO = {
  // Scope 2 - Electricity
  'electricity': { label: 'Grid Electricity', unit: 'kWh', scope: 'Scope 2' },
  'electricity_greenpower': { label: 'GreenPower (Renewable)', unit: 'kWh', scope: 'Scope 2' },

  // Scope 1 - Transport Fuels
  'fuel_petrol': { label: 'Petrol', unit: 'L', scope: 'Scope 1' },
  'fuel_diesel': { label: 'Diesel', unit: 'L', scope: 'Scope 1' },
  'fuel_lpg': { label: 'LPG (Vehicle)', unit: 'L', scope: 'Scope 1' },
  'fuel_e10': { label: 'E10 Petrol', unit: 'L', scope: 'Scope 1' },

  // Scope 1 - Stationary Energy
  'natural_gas': { label: 'Natural Gas', unit: 'GJ', scope: 'Scope 1' },
  'stationary_lpg': { label: 'LPG (Stationary)', unit: 'L', scope: 'Scope 1' },
  'stationary_diesel': { label: 'Diesel (Generator)', unit: 'L', scope: 'Scope 1' },

  // Scope 1 - Refrigerants
  'refrigerant_r134a': { label: 'Refrigerant R134A', unit: 'kg', scope: 'Scope 1' },
  'refrigerant_r410a': { label: 'Refrigerant R410A', unit: 'kg', scope: 'Scope 1' },
  'refrigerant_r32': { label: 'Refrigerant R32', unit: 'kg', scope: 'Scope 1' },
  'refrigerant_r404a': { label: 'Refrigerant R404A', unit: 'kg', scope: 'Scope 1' },
  'refrigerant_r22': { label: 'Refrigerant R22 (Legacy)', unit: 'kg', scope: 'Scope 1' },
};

/**
 * Calculate CO2 emissions for a given category, quantity, state, and financial year
 * 
 * @param {string} category - Emission category (e.g., 'electricity', 'fuel_petrol')
 * @param {number} quantity - Amount in the category's input unit
 * @param {string} state - Australian state code (for electricity) - can include grid region (WA_SWIS)
 * @param {number} financialYear - Optional financial year (e.g., 2026 for FY 2025-26). Uses latest if not provided.
 * @returns {Object} { co2_equivalent_kg, co2_equivalent_t, scope, factor_used, factor_unit, factor_source }
 */
export function calculateEmissions(category, quantity, state = 'NSW', financialYear = null) {
  if (!quantity || quantity <= 0) {
    return {
      co2_equivalent_kg: 0,
      co2_equivalent_t: 0,
      scope: SCOPE_MAPPING[category?.toLowerCase()] || 'Unknown',
      factor_used: null,
      factor_unit: null,
      factor_source: null
    };
  }

  const catLower = category?.toLowerCase();
  let factor = 0;
  let factorUnit = '';
  const scope = SCOPE_MAPPING[catLower] || 'Unknown';

  // ==================== SCOPE 2: Electricity ====================
  if (catLower === 'electricity') {
    const stateUpper = state?.toUpperCase();
    const { factor: elecFactor, ngaSource } = getElectricityFactor(stateUpper, financialYear);
    factor = elecFactor;
    factorUnit = 'kg CO2-e/kWh';

    const co2_kg = quantity * factor;
    return {
      co2_equivalent_kg: co2_kg,
      co2_equivalent_t: co2_kg / 1000,
      scope,
      factor_used: factor,
      factor_unit: factorUnit,
      factor_source: `${ngaSource} (${stateUpper || 'Default'})`
    };
  }

  if (catLower === 'electricity_greenpower') {
    return {
      co2_equivalent_kg: 0,
      co2_equivalent_t: 0,
      scope,
      factor_used: GREENPOWER_FACTOR,
      factor_unit: 'kg CO2-e/kWh',
      factor_source: 'GreenPower (Zero Emission)'
    };
  }

  // ==================== SCOPE 1: Transport Fuels ====================
  if (catLower.startsWith('fuel_')) {
    const fuelType = catLower.replace('fuel_', '');
    const { factor: fuelFactor, ngaSource } = getFuelFactor(fuelType, financialYear);
    factor = fuelFactor;
    factorUnit = 'kg CO2-e/L';

    const co2_kg = quantity * factor;
    return {
      co2_equivalent_kg: co2_kg,
      co2_equivalent_t: co2_kg / 1000,
      scope,
      factor_used: factor,
      factor_unit: factorUnit,
      factor_source: ngaSource
    };
  }

  // ==================== SCOPE 1: Stationary Energy ====================
  if (catLower === 'natural_gas') {
    const { factor: gasFactor, ngaSource } = getNaturalGasFactor(financialYear);
    factor = gasFactor;
    factorUnit = 'kg CO2-e/GJ';
    const co2_kg = quantity * factor;
    return {
      co2_equivalent_kg: co2_kg,
      co2_equivalent_t: co2_kg / 1000,
      scope,
      factor_used: factor,
      factor_unit: factorUnit,
      factor_source: ngaSource
    };
  }

  if (catLower === 'stationary_lpg') {
    const { ngaSource } = getFuelFactor('lpg', financialYear);
    factor = STATIONARY_LPG_FACTOR;
    factorUnit = 'kg CO2-e/L';
    const co2_kg = quantity * factor;
    return {
      co2_equivalent_kg: co2_kg,
      co2_equivalent_t: co2_kg / 1000,
      scope,
      factor_used: factor,
      factor_unit: factorUnit,
      factor_source: ngaSource
    };
  }

  if (catLower === 'stationary_diesel') {
    const { ngaSource } = getFuelFactor('diesel', financialYear);
    factor = STATIONARY_DIESEL_FACTOR;
    factorUnit = 'kg CO2-e/L';
    const co2_kg = quantity * factor;
    return {
      co2_equivalent_kg: co2_kg,
      co2_equivalent_t: co2_kg / 1000,
      scope,
      factor_used: factor,
      factor_unit: factorUnit,
      factor_source: ngaSource
    };
  }

  // ==================== SCOPE 1: Refrigerants ====================
  if (catLower.startsWith('refrigerant_')) {
    const refType = catLower.replace('refrigerant_', '');
    factor = REFRIGERANT_FACTORS[refType] || 1000; // Default GWP if unknown
    factorUnit = 'kg CO2-e/kg';
    const { ngaSource } = getNaturalGasFactor(financialYear); // Use any factor to get the year

    const co2_kg = quantity * factor;
    return {
      co2_equivalent_kg: co2_kg,
      co2_equivalent_t: co2_kg / 1000,
      scope,
      factor_used: factor,
      factor_unit: factorUnit,
      factor_source: ngaSource
    };
  }

  // ==================== SCOPE 3: Waste ====================
  // Note: Scope 3 categories are not actively tracked but kept for backwards compatibility
  if (catLower.startsWith('waste_') || catLower === 'waste') {
    const wasteType = catLower.replace('waste_', '') || 'generic';
    factor = WASTE_FACTORS[wasteType] || WASTE_FACTORS['generic'];
    factorUnit = 'kg CO2-e/t';
    const { ngaSource } = getNaturalGasFactor(financialYear);

    const co2_kg = quantity * factor;
    return {
      co2_equivalent_kg: co2_kg,
      co2_equivalent_t: co2_kg / 1000,
      scope,
      factor_used: factor,
      factor_unit: factorUnit,
      factor_source: ngaSource
    };
  }

  // ==================== SCOPE 3: Flights ====================
  if (catLower.startsWith('flight_')) {
    const flightType = catLower.replace('flight_', '');
    factor = FLIGHT_FACTORS[flightType] || FLIGHT_FACTORS['medium'];
    factorUnit = 'kg CO2-e/pax-km';

    const co2_kg = quantity * factor;
    return {
      co2_equivalent_kg: co2_kg,
      co2_equivalent_t: co2_kg / 1000,
      scope,
      factor_used: factor,
      factor_unit: factorUnit,
      factor_source: 'DEFRA 2024'
    };
  }

  // ==================== SCOPE 3: Hotel ====================
  if (catLower === 'hotel_stay') {
    factor = HOTEL_FACTOR;
    factorUnit = 'kg CO2-e/night';
    const co2_kg = quantity * factor;
    return {
      co2_equivalent_kg: co2_kg,
      co2_equivalent_t: co2_kg / 1000,
      scope,
      factor_used: factor,
      factor_unit: factorUnit,
      factor_source: 'DEFRA 2024'
    };
  }

  // ==================== SCOPE 3: Water ====================
  // Note: Scope 3 categories are not actively tracked but kept for backwards compatibility
  if (catLower === 'water') {
    factor = WATER_FACTOR;
    factorUnit = 'kg CO2-e/kL';
    const { ngaSource } = getNaturalGasFactor(financialYear);
    const co2_kg = quantity * factor;
    return {
      co2_equivalent_kg: co2_kg,
      co2_equivalent_t: co2_kg / 1000,
      scope,
      factor_used: factor,
      factor_unit: factorUnit,
      factor_source: ngaSource
    };
  }

  // Unknown category
  console.warn(`Unknown emission category: ${category}`);
  return {
    co2_equivalent_kg: 0,
    co2_equivalent_t: 0,
    scope: 'Unknown',
    factor_used: null,
    factor_unit: null,
    factor_source: null
  };
}

/**
 * Get the scope for a given category
 */
export function getScope(category) {
  return SCOPE_MAPPING[category?.toLowerCase()] || 'Unknown';
}

/**
 * Get emission factor info for display purposes
 */
export function getEmissionFactorInfo(category, state = 'NSW') {
  const catLower = category?.toLowerCase();
  const info = CATEGORY_INFO[catLower];

  if (info) {
    let factor = null;

    if (catLower === 'electricity') {
      factor = STATE_ELECTRICITY_FACTORS[state?.toUpperCase()] || DEFAULT_ELECTRICITY_FACTOR;
    } else if (catLower === 'electricity_greenpower') {
      factor = 0;
    } else if (catLower.startsWith('fuel_')) {
      const fuelType = catLower.replace('fuel_', '');
      factor = FUEL_FACTORS[fuelType] || FUEL_FACTORS['blended'];
    } else if (catLower === 'natural_gas') {
      factor = NATURAL_GAS_FACTOR;
    } else if (catLower.startsWith('waste_')) {
      const wasteType = catLower.replace('waste_', '');
      factor = WASTE_FACTORS[wasteType] || WASTE_FACTORS['generic'];
    } else if (catLower.startsWith('flight_')) {
      const flightType = catLower.replace('flight_', '');
      factor = FLIGHT_FACTORS[flightType];
    } else if (catLower === 'hotel_stay') {
      factor = HOTEL_FACTOR;
    } else if (catLower === 'water') {
      factor = WATER_FACTOR;
    } else if (catLower.startsWith('refrigerant_')) {
      const refType = catLower.replace('refrigerant_', '');
      factor = REFRIGERANT_FACTORS[refType];
    }

    return {
      factor,
      unit: `kg CO2-e/${info.unit}`,
      scope: info.scope,
      label: info.label,
      inputUnit: info.unit
    };
  }

  return { factor: null, unit: null, scope: null, label: null, inputUnit: null };
}

/**
 * Convert kg CO2-e to tonnes CO2-e
 */
export function kgToTonnes(kgCO2e) {
  return kgCO2e / 1000;
}

/**
 * Format emissions for display (always shows tCO2-e)
 */
export function formatEmissions(kgCO2e, decimals = 2) {
  const tonnes = kgCO2e / 1000;
  return `${tonnes.toFixed(decimals)} tCO₂-e`;
}

/**
 * Calculate emissions for an entry spanning multiple financial years
 * Applies the correct NGA factor for each year's apportioned quantity
 * 
 * @param {string} category - Emission category
 * @param {number} quantity - Total quantity for the period
 * @param {string} state - Australian state/grid region
 * @param {Date|string} startDate - Period start date
 * @param {Date|string} endDate - Period end date
 * @returns {Object} { total_co2e_t, allocations: [{ fy, fyYear, quantity, co2e_t, factor, factor_source }] }
 */
export function calculateMultiYearEmissions(category, quantity, state, startDate, endDate) {
  // Always work with UTC midnight dates to avoid daylight savings/timezone issues spanning dates
  const parseDateUTC = (dateString) => {
    if (!dateString) return null;
    const d = new Date(dateString);
    // If it's a valid date string without time components ('YYYY-MM-DD'),
    // new Date('2025-05-01') is parsed as UTC midnight
    // We just want to ensure we're comparing timestamps safely
    return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  };

  const start = parseDateUTC(startDate);
  const end = endDate ? parseDateUTC(endDate) : parseDateUTC(startDate);

  // If same date or no end date, use single calculation
  if (!endDate || start.getTime() === end.getTime()) {
    const fy = start.getUTCMonth() >= 6 ? start.getUTCFullYear() + 1 : start.getUTCFullYear();
    const result = calculateEmissions(category, quantity, state, fy);
    return {
      total_co2e_t: result.co2_equivalent_t,
      allocations: [{
        fy: `FY ${fy - 1}-${String(fy).slice(-2)}`,
        fyYear: fy,
        quantity: quantity,
        co2e_t: result.co2_equivalent_t,
        factor: result.factor_used,
        factor_source: result.factor_source
      }]
    };
  }

  // Calculate days in each FY and apportion quantity
  const totalDays = Math.max(1, Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1);
  const allocations = [];
  let totalCo2e = 0;

  let currentFY = start.getUTCMonth() >= 6 ? start.getUTCFullYear() + 1 : start.getUTCFullYear();
  let currentDate = new Date(start.getTime()); // copy

  while (currentDate <= end) {
    // Get FY date range
    const fyStart = new Date(Date.UTC(currentFY - 1, 6, 1)); // July 1
    const fyEnd = new Date(Date.UTC(currentFY, 5, 30)); // June 30

    // Calculate days in this FY
    const periodStart = currentDate > fyStart ? currentDate : fyStart;
    const periodEnd = end < fyEnd ? end : fyEnd;
    const daysInFY = Math.max(0, Math.round((periodEnd - periodStart) / (1000 * 60 * 60 * 24)) + 1);

    if (daysInFY > 0) {
      // Pro-rata quantity for this FY
      const fyQuantity = (quantity * daysInFY) / totalDays;

      // Calculate emissions using year-specific factor
      const result = calculateEmissions(category, fyQuantity, state, currentFY);

      totalCo2e += result.co2_equivalent_t;

      allocations.push({
        fy: `FY ${currentFY - 1}-${String(currentFY).slice(-2)}`,
        fyYear: currentFY,
        quantity: fyQuantity,
        days: daysInFY,
        co2e_t: result.co2_equivalent_t,
        factor: result.factor_used,
        factor_source: result.factor_source
      });
    }

    // Move to next FY
    currentFY++;
    currentDate = new Date(Date.UTC(currentFY - 1, 6, 1));
  }

  return {
    total_co2e_t: totalCo2e,
    allocations
  };
}
